import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from 'common/elements/Loader/Loader';
import { debounce } from 'lodash/function';
import { isEmpty } from 'lodash/lang';

import ApplyAndResetButtons from 'components/ApplyAndResetButtons/ApplyAndResetButtons';
import BasicMultiselect from 'components/eui/EuiFiltersContext/controls/BasicMultiselect/BasicMultiselect';
import { useEuiFiltersDispatch, useEuiFiltersState } from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import { setCacheProcessor, setInitialStateProcessor } from 'components/eui/EuiFiltersContext/processors';
import { ACTION_PERFORM, ACTION_PERFORM_INITIALIZATION, FRS_READY } from 'components/eui/EuiFiltersContext/reducer';
import { PageInfoHeader } from 'components/PageInfoHeader/PageInfoHeader';
import PatientsCountHelperText from 'components/pages/pre-screen/PatientDatabase/FiltersSection/PatientsCountHelperText';
import { hasActiveFilters } from 'components/pages/pre-screen/PatientDatabase/PatientDatabaseService';

import { PatientDatabaseApi } from '../../../../../api';
import useAppInfo from '../../../../../common/hooks/useAppInfo';
import { onRequestError } from '../../../../../services/handlers';
import { INITIAL_FILTERS_STATE } from '../constants';

import { cacheCollector, getStudySites } from './FiltersSectionService';
import PatientDatabaseStudySiteMultiselect from './PatientDatabaseStudySiteMultiselect';
import { setValueProcessor } from './patientDatabaseStudySiteProcessors';

export const COUNT_DEBOUNCE_MS = 500;
const debounceCache = debounce(cb => cb(), COUNT_DEBOUNCE_MS);

export default function FiltersSection({ onApply, alreadyLoaded, setAlreadyLoaded, request }) {
  const state = useEuiFiltersState();
  const dispatch = useEuiFiltersDispatch();

  const { patientDatabaseThresholdCount } = useAppInfo();
  const [patientsFilterCountModel, setPatientsFilterCountModel] = useState(null);
  const [patientsCountError, setPatientsCountError] = useState(false);
  const [patientsFilterCountIsLoading, setPatientsFilterCountIsLoading] = useState(false);

  const { schema } = state;
  const status = state.schema?.status;

  useEffect(
    function() {
      if (status !== FRS_READY) {
        return;
      }
      debounceCache(function() {
        dispatch({
          type: ACTION_PERFORM,
          processor: setCacheProcessor
        });
      });
    },
    [dispatch, status]
  );

  useEffect(
    function() {
      if (status) return;
      dispatch({
        type: ACTION_PERFORM_INITIALIZATION,
        processor: setInitialStateProcessor,
        payload: {
          ...INITIAL_FILTERS_STATE,
          cacheCollector
        }
      });
    },
    [dispatch, status]
  );

  const loadFilterPatientsCount = useCallback(async function(request) {
    return PatientDatabaseApi.getPatientsCountByFilters(request);
  }, []);

  useEffect(
    function() {
      setAlreadyLoaded(false);
      setPatientsCountError(false);

      if (!request || !hasActiveFilters(request)) {
        setPatientsFilterCountIsLoading(false);
        return;
      }

      let cancelled = false;
      setPatientsFilterCountIsLoading(true);

      const timerId = setTimeout(function() {
        loadFilterPatientsCount(request)
          .then(
            ({ data }) => {
              if (!cancelled) setPatientsFilterCountModel(data);
            },
            err => {
              if (cancelled) return;
              setPatientsFilterCountModel(null);
              setPatientsCountError(true);
              onRequestError(err);
            }
          )
          .finally(() => {
            if (!cancelled) setPatientsFilterCountIsLoading(false);
          });
      }, COUNT_DEBOUNCE_MS);

      return function() {
        cancelled = true;
        clearTimeout(timerId);
      };
    },
    [loadFilterPatientsCount, request, setAlreadyLoaded]
  );

  const applyDisabled = useMemo(() => {
    return (
      alreadyLoaded ||
      !request ||
      !hasActiveFilters(request) ||
      !patientsFilterCountModel ||
      patientsFilterCountIsLoading ||
      (patientsFilterCountModel &&
        Number(patientsFilterCountModel.filterPatientsCount) > Number(patientDatabaseThresholdCount))
    );
  }, [alreadyLoaded, patientDatabaseThresholdCount, patientsFilterCountIsLoading, patientsFilterCountModel, request]);

  if (isEmpty(schema)) return null;

  return (
    <PageInfoHeader>
      <div className="general-header-wrapper" style={{ minHeight: 71 }}>
        <PatientDatabaseStudySiteMultiselect
          uniqKey="ssu"
          state={state}
          dispatch={dispatch}
          getStudySites={getStudySites}
        />
        <BasicMultiselect
          uniqKey="therapeuticArea"
          state={state}
          dispatch={dispatch}
          label="Therapeutic Area"
          initializeOptions={false}
          setValueProcessor={setValueProcessor}
          syncCache={false}
          data-testid="therapeutic-area-filter"
        />
        <BasicMultiselect
          uniqKey="indication"
          state={state}
          dispatch={dispatch}
          label="Indication"
          initializeOptions={false}
          setValueProcessor={setValueProcessor}
          syncCache={false}
          data-testid="indication-filter"
        />
        {status === FRS_READY ? (
          <ApplyAndResetButtons
            applyButtonId="patient-database-filter-apply-btn"
            applyClassName="patient-database-apply-btn"
            onApply={onApply}
            applyDisabled={applyDisabled}
          />
        ) : (
          <Loader dark={true} />
        )}
      </div>
      <PatientsCountHelperText
        patientsFilterCountModel={patientsFilterCountModel}
        patientDatabaseThresholdCount={patientDatabaseThresholdCount}
        filtersState={state}
        hasError={patientsCountError}
      />
    </PageInfoHeader>
  );
}
